import { AlertTriangle, RotateCw, Trash2 } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STRUCTURE_EDITOR_BBX_THRESHOLDS } from '../../../../../lib/constants.js';
import { cn } from '../../../../../lib/utils.js';
import { normalizeBox, type PercentBox } from '../utils.js';

interface BoxOverlayProps {
  box: PercentBox;
  selected: boolean;
  isReadOnly: boolean;
  color?: 'blue' | 'purple';
  onSelect: () => void;
  onInteractionEnd: (boxId: string, didChange: boolean) => void;
  onChange: (next: PercentBox) => void;
  onDelete: () => void;
  onRetry?: () => void | Promise<void>;
}

export function BoxOverlay({
  box,
  selected,
  isReadOnly,
  color = 'blue',
  onSelect,
  onInteractionEnd,
  onChange,
  onDelete,
  onRetry,
}: BoxOverlayProps) {
  const { t } = useTranslation(); // <-- NEU
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRetrying) return;
    try {
      setIsRetrying(true);
      await onRetry?.();
    } finally {
      setIsRetrying(false);
    }
  };

  const startInteraction = (e: React.PointerEvent, action: 'move' | 'nw' | 'ne' | 'sw' | 'se') => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();

    const container = containerRef.current?.parentElement?.parentElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startBox = { ...box };
    let didChange = false;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const scale = rect.width / 800;
      const deltaPxX = (moveEvent.clientX - startX) / scale;
      const deltaPxY = (moveEvent.clientY - startY) / scale;
      const deltaPctX = (deltaPxX / 800) * 100;
      const deltaPctY = (deltaPxY / (rect.height / scale)) * 100;

      const next = { ...startBox };
      if (action === 'move') {
        next.x += deltaPctX;
        next.y += deltaPctY;
      } else {
        if (action.includes('n')) {
          next.y += deltaPctY;
          next.height -= deltaPctY;
        }
        if (action.includes('s')) next.height += deltaPctY;
        if (action.includes('w')) {
          next.x += deltaPctX;
          next.width -= deltaPctX;
        }
        if (action.includes('e')) next.width += deltaPctX;
      }

      if (next.width > 1 && next.height > 1) {
        onChange(normalizeBox(next));
        didChange = true;
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      onInteractionEnd(box.id, didChange);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleClass = cn(
    'absolute w-3 h-3 rounded-full z-20 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center',
    selected ? (color === 'purple' ? 'bg-purple-700' : 'bg-blue-700') : 'bg-slate-700',
  );

  const overlayClass = cn(
    'absolute border-2 transition-colors group',
    isReadOnly ? 'cursor-pointer' : 'cursor-move',
    color === 'purple'
      ? selected
        ? 'border-blue-600 bg-blue-500/25 z-20'
        : 'border-sky-400 bg-sky-400/10 hover:bg-sky-400/20 z-10'
      : selected
        ? 'border-blue-600 bg-blue-500/25 z-20'
        : 'border-sky-400 bg-sky-400/10 hover:bg-sky-400/20 z-10',
  );

  return (
    <div
      ref={containerRef}
      className={overlayClass}
      style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.width}%`, height: `${box.height}%` }}
      onClick={e => {
        e.stopPropagation();
        onSelect();
      }}
      onPointerDown={isReadOnly ? undefined : e => startInteraction(e, 'move')}
    >
      {!isReadOnly && (
        <div className="absolute -top-8 left-1/2 z-30 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <div className="flex items-center overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-card shadow-sm">
            <button
              type="button"
              className="border-r border-slate-200 dark:border-slate-700 p-1.5 text-slate-600 dark:text-slate-400 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600"
              onPointerDown={e => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              title={t('Canvas.structureEditor.deleteBbx')}
            >
              <Trash2 size={12} />
            </button>
            {onRetry && (
              <button
                type="button"
                className="p-1.5 text-slate-600 dark:text-slate-400 transition-colors hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:text-sky-600 disabled:opacity-60"
                onPointerDown={handleRetry}
                disabled={isRetrying}
                title={t('Canvas.structureEditor.retryOcr')}
              >
                <RotateCw size={12} className={cn(isRetrying && 'animate-spin')} />
              </button>
            )}
          </div>
        </div>
      )}
      {!isReadOnly && (
        <>
          <div
            className={cn(handleClass, '-top-1.5 -left-1.5 cursor-nwse-resize')}
            onPointerDown={e => startInteraction(e, 'nw')}
          />
          <div
            className={cn(handleClass, '-top-1.5 -right-1.5 cursor-nesw-resize')}
            onPointerDown={e => startInteraction(e, 'ne')}
          />
          <div
            className={cn(handleClass, '-bottom-1.5 -left-1.5 cursor-nesw-resize')}
            onPointerDown={e => startInteraction(e, 'sw')}
          />
          <div
            className={cn(handleClass, '-bottom-1.5 -right-1.5 cursor-nwse-resize')}
            onPointerDown={e => startInteraction(e, 'se')}
          />
        </>
      )}
      {!selected &&
        box.confidence !== undefined &&
        box.confidence < STRUCTURE_EDITOR_BBX_THRESHOLDS.HIGH_CONFIDENCE && (
          <AlertTriangle
            className={cn(
              'absolute -left-4 top-1/2 -translate-y-1/2 z-30 pointer-events-none drop-shadow-sm',
              box.confidence < STRUCTURE_EDITOR_BBX_THRESHOLDS.LOW_CONFIDENCE ? 'text-red-500' : 'text-amber-400',
            )}
            size={12}
          />
        )}
    </div>
  );
}
